import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  LucideAngularModule,
  Search,
  X,
  Edit,
  CircleIcon,
} from 'lucide-angular';
import { ReferentielService } from '../../../../core/services/referentiel.service';
import { InsuranceService } from '../../../../core/services/insurance.service';
import { Subject, takeUntil } from 'rxjs';
import { ThemeService } from '../../../../core/services/theme.service';

export interface Provider {
  id: string;
  name: string;
  type: 'doctor' | 'pharmacy' | 'medical_structure' | 'laboratory';
  eyoneExternalId?: string;
  eyoneUser?: boolean;
}

@Component({
  selector: 'app-provider-autocomplete',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './provider-autocomplete.component.html',
  styleUrls: ['./provider-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProviderAutocompleteComponent),
      multi: true,
    },
  ],
})
export class ProviderAutocompleteComponent
  implements OnInit, ControlValueAccessor, OnDestroy
{
  @Input() placeholder: string = 'Rechercher un prestataire...';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() label: string = 'Prestataire';
  @Input() insurerExternalId: string = '';
  @Input() messageType: string = '';
  @Output() providerSelected = new EventEmitter<Provider>();
  @Output() validateNonConventionnedProvider = new EventEmitter<{
    searchTerm: string;
  }>();
  @Output() validationComplete = new EventEmitter<void>();
  @Output() validationRequested = new EventEmitter<{ searchTerm: string }>();
  @Output() providerTypeChanged = new EventEmitter<
    'conventionnes' | 'non_conventionnes'
  >();

  readonly Search = Search;
  readonly X = X;
  readonly Edit = Edit;
  readonly CircleIcon = CircleIcon;

  searchTerm: string = '';
  filteredProviders: Provider[] = [];
  isOpen: boolean = false;
  loading: boolean = false;
  selectedProvider: Provider | null = null;
  error: string | null = null;
  providerSearchType: 'conventionnes' | 'non_conventionnes' = 'conventionnes';

  private destroy$ = new Subject<void>();

  // ControlValueAccessor implementation
  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private referentielService: ReferentielService,
    private insuranceService: InsuranceService,
    private themeService: ThemeService,
  ) {}

  ngOnInit(): void {
    // Ne plus charger tous les prestataires au début
    // La recherche se fera via onSearchInput
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private searchProviders(searchTerm: string): void {
    if (!this.insurerExternalId || searchTerm.length < 2) {
      this.filteredProviders = [];
      this.isOpen = false;
      return;
    }

    this.loading = true;
    this.error = null;

    // Choose the appropriate service based on provider search type
    if (this.providerSearchType === 'non_conventionnes') {
      // Use insurance service for non-conventionnés providers
      this.insuranceService
        .getNonConventionnedProviders(this.insurerExternalId, searchTerm)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.loading = false;
            if (response) {
              this.filteredProviders =
                this.transformNonConventionnedData(response);
              this.isOpen = this.filteredProviders.length > 0;

              // Don't trigger modal automatically when typing - only on submit
            }
          },
          error: (error) => {
            console.error(
              'Erreur lors de la recherche des prestataires non conventionnés:',
              error,
            );
            this.loading = false;
            this.error =
              'Erreur lors de la recherche des prestataires non conventionnés';
            this.filteredProviders = [];
            this.isOpen = false;
          },
        });
    } else {
      // Use referentiel service for conventionnés providers (default)
      const searchParams = {
        search: searchTerm,
        insurerEyoneExternalId: this.insurerExternalId,
      };
      console.log('searchParams', searchParams);

      this.referentielService
        .searchAutocomplete(searchParams)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.loading = false;
            if (response) {
              this.filteredProviders = this.transformInsurerData(response);
              this.isOpen = this.filteredProviders.length > 0;
            }
          },
          error: (error) => {
            console.error(
              'Erreur lors de la recherche des prestataires:',
              error,
            );
            this.loading = false;
            this.error = 'Erreur lors de la recherche des prestataires';
            this.filteredProviders = [];
            this.isOpen = false;
          },
        });
    }
  }

  private transformInsurerData(data: any[]): Provider[] {
    const providers: Provider[] = [];

    if (Array.isArray(data)) {
      data.forEach((item: any) => {
        let providerType:
          | 'doctor'
          | 'pharmacy'
          | 'medical_structure'
          | 'laboratory';

        // Mapper les types de l'API vers les types du composant
        switch (item.type) {
          case 'doctor':
            providerType = 'doctor';
            break;
          case 'pharmacy':
            providerType = 'pharmacy';
            break;
          case 'structure':
            providerType = 'medical_structure';
            break;
          default:
            providerType = 'medical_structure';
        }

        providers.push({
          id: item.id?.toString() || item.eyoneExternalId,
          name: item.result,
          type: providerType,
          eyoneExternalId: item.eyoneExternalId,
          eyoneUser: item.eyoneUser,
        });
      });
    }

    return providers;
  }

  private transformNonConventionnedData(data: any): Provider[] {
    const providers: Provider[] = [];

    // Handle the response structure from getNonConventionnedProviders
    if (data && Array.isArray(data)) {
      data.forEach((item: any) => {
        let providerType:
          | 'doctor'
          | 'pharmacy'
          | 'medical_structure'
          | 'laboratory';

        // Map the types from the non-conventionnés API
        switch (item.type) {
          case 'doctor':
          case 'medecin':
            providerType = 'doctor';
            break;
          case 'pharmacy':
          case 'pharmacie':
            providerType = 'pharmacy';
            break;
          case 'laboratory':
          case 'laboratoire':
            providerType = 'laboratory';
            break;
          case 'structure':
          case 'medical_structure':
            providerType = 'medical_structure';
            break;
          default:
            providerType = 'medical_structure';
        }

        providers.push({
          id: item.id?.toString() || item.externalId || item.eyoneExternalId,
          name: item.name || item.result || item.label,
          type: providerType,
          eyoneExternalId: item.eyoneExternalId || item.externalId,
          eyoneUser: item.eyoneUser,
        });
      });
    }

    return providers;
  }

  onSearchInput(event: any): void {
    const query = event.target.value;
    this.searchTerm = query;

    // Utiliser la nouvelle méthode de recherche avec l'API
    this.searchProviders(query);
  }

  selectProvider(provider: Provider): void {
    this.selectedProvider = provider;
    this.searchTerm = provider.name;
    this.isOpen = false;
    this.onChange(provider);
    this.onTouched();
    this.providerSelected.emit(provider);
  }

  clearSelection(): void {
    this.selectedProvider = null;
    this.searchTerm = '';
    this.isOpen = false;
    this.onChange(null);
    this.onTouched();
  }

  setProviderSearchType(type: 'conventionnes' | 'non_conventionnes'): void {
    const previousType = this.providerSearchType;
    this.providerSearchType = type;

    // Clear current search results and selection
    this.filteredProviders = [];
    this.isOpen = false;
    this.selectedProvider = null;
    this.searchTerm = '';
    this.onChange(null);
    this.onTouched();

    // Emit event to parent component
    this.providerTypeChanged.emit(type);

    // Re-search if there is a search term (though it should be cleared now)
    if (this.searchTerm.length >= 2) {
      this.searchProviders(this.searchTerm);
    }
  }

  onFocus(): void {
    if (this.searchTerm.length >= 2 && this.filteredProviders.length > 0) {
      this.isOpen = true;
    }
  }

  onBlur(): void {
    // Délai pour permettre la sélection avant de fermer
    setTimeout(() => {
      this.isOpen = false;
    }, 200);
  }

  getProviderIcon(type: string): string {
    switch (type) {
      case 'doctor':
        return '';
      case 'pharmacy':
        return '';
      case 'laboratory':
        return '';
      case 'medical_structure':
        return '';
      default:
        return '';
    }
  }

  getProviderTypeLabel(type: string): string {
    switch (type) {
      case 'doctor':
        return 'Médecin';
      case 'pharmacy':
        return 'Pharmacie';
      case 'laboratory':
        return 'Laboratoire';
      case 'medical_structure':
        return 'Structure';
      default:
        return 'Prestataire';
    }
  }

  trackByProvider(index: number, provider: Provider): string {
    return provider.id;
  }

  // ControlValueAccessor methods
  writeValue(value: Provider | null): void {
    this.selectedProvider = value;
    if (value) {
      this.searchTerm = value.name;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Method to handle validation completion from parent
  handleValidationComplete(providerData: { name: string; type: string }): void {
    // Create a custom provider object for non-conventionnés
    const customProvider: Provider = {
      id: 'custom-' + Date.now(),
      name: providerData.name.trim(),
      type: this.getProviderTypeFromSelection(providerData.type),
      eyoneExternalId: undefined,
    };

    // Update search term and selected provider
    this.searchTerm = providerData.name.trim();
    this.selectedProvider = customProvider;

    // Emit the provider with additional healthProType info
    this.providerSelected.emit({
      ...customProvider,
      healthProType: providerData.type,
    } as any);
  }

  private getProviderTypeFromSelection(
    selection: string,
  ): 'doctor' | 'pharmacy' | 'medical_structure' | 'laboratory' {
    switch (selection) {
      case 'DOCTOR':
        return 'doctor';
      case 'PHARMACY':
        return 'pharmacy';
      case 'HOSPITAL':
        return 'medical_structure';
      default:
        return 'medical_structure';
    }
  }

  // Method to check if validation is needed for submission
  needsValidation(): boolean {
    return (
      this.messageType === 'REFUND_REQUEST' &&
      this.providerSearchType === 'non_conventionnes' &&
      this.searchTerm.length >= 2 &&
      !this.selectedProvider
    );
  }

  // Method to trigger validation from parent component
  triggerValidation(): void {
    if (this.needsValidation()) {
      this.validationRequested.emit({ searchTerm: this.searchTerm });
    }
  }

  // Nom du produit selon le thème (FA'JMA ou Passeport Médical)
  getProductName(): string {
    return this.themeService.getProductName();
  }
}
